import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';
import { webSocketService } from '../services/websocket';
import { Application, ApplicationDetails, FileUploadType, FileTypeConfig } from '../types/models';
import { useAppContext } from '../context/AppContext';
import { DocumentPlusIcon, ExclamationTriangleIcon, InboxIcon, CalendarDaysIcon, HashtagIcon, ArrowRightIcon, ArrowUpTrayIcon, DocumentTextIcon, BoltIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import EditableComment from '../components/EditableComment';
import ReportModal from '../components/ReportModal';
import ApplicationStatusBadge from '../components/ApplicationStatusBadge';
import { Console } from 'console';
import { json } from 'stream/consumers';
import { trackGoal } from '../utils/metrika';


const UserApplicationsPage: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, userId } = useAppContext();
  const navigate = useNavigate();

  // Состояния для загрузки файлов разных типов
  const [selectedFiles, setSelectedFiles] = useState<{ [key in FileUploadType]?: File }>({});
  const [uploading, setUploading] = useState<{ [key in FileUploadType]?: boolean }>({});
  const [uploadErrors, setUploadErrors] = useState<{ [key in FileUploadType]?: string }>({});
  const [uploadSuccessMessages, setUploadSuccessMessages] = useState<{ [key in FileUploadType]?: string }>({});
  const [isDragging, setIsDragging] = useState<{ [key in FileUploadType]?: boolean }>({});
  
  const [processingApplications, setProcessingApplications] = useState<Set<number>>(new Set());
  
  // Маппинг имен файлов на UI поля (для корректного отображения ошибок WebSocket)
  // Используем ref вместо state чтобы избежать проблем с stale closures в handleMessage
  // Жизненный цикл: 
  // 1. Файл загружается через UI поле (например, electrical_scheme)
  // 2. На бэкенд отправляется преобразованный тип (например, technical_doc)
  // 3. При ошибке WebSocket возвращает file_type с бэкенда (technical_doc)
  // 4. Маппинг используется для обратной трансляции (technical_doc -> electrical_scheme)
  // 5. Ошибка отображается в правильном UI поле (electrical_scheme)
  // 6. Очистка маппинга:
  //    - Успешная обработка: очищается при материализации заявки (application_status_changed)
  //    - Ошибка обработки: маппинг остается, но будет перезаписан при следующей загрузке
  //      файла с таким же именем (т.е. пользователь сам исправляет файл и загружает снова)
  //    - Потенциальная утечка памяти ограничена типичным использованием: пользователи редко
  //      загружают сотни уникальных файлов с ошибками без повторных попыток
  const filenameToFieldMapRef = useRef<{ [filename: string]: FileUploadType }>({});
  
  // Состояния для модального окна отчета
  const [reportModal, setReportModal] = useState<{ isOpen: boolean; application: ApplicationDetails | null }>({
    isOpen: false,
    application: null
  });

  // Конфигурация типов файлов
  const fileTypeConfigs: FileTypeConfig[] = [
    {
      key: 'specification',
      title: 'Спецификация',
      description: 'Загрузите перечень номенклатуры для подбора артикулов',
      acceptedFormats: '.pdf,.xlsx,.xls,.doc,.docx',
      icon: 'document'
    },
    {
      key: 'electrical_scheme',
      title: 'Однолинейная электрическая схема',
      description: 'Загрузите однолинейную схему для анализа',
      acceptedFormats: '.pdf,.dwg,.jpg,.png,.jpeg',
      icon: 'bolt'
    },
    {
      key: 'technical_doc',
      title: 'Техническая документация',
      description: 'Загрузите техническую документацию проекта',
      acceptedFormats: '.pdf,.doc,.docx,.txt',
      icon: 'chart'
    }
  ];

  // Helper function для очистки маппинга имени файла
  const cleanupFilenameMapping = useCallback((filename: string | null) => {
    if (filename && filenameToFieldMapRef.current[filename]) {
      delete filenameToFieldMapRef.current[filename];
    }
  }, []);

  const handleMessage = useCallback((message: any) => {
    console.log('WebSocket message received in UserApplicationsPage:', message);
    
    // Обработка нового унифицированного события application_status_changed
    if (message.event === 'application_status_changed') {
      const updatedApp = message.application as Application;
      console.log('Application status changed via WebSocket:', updatedApp.id, updatedApp.processing_status);
      
      setApplications(prev => {
        // Шаг 1: Сначала проверяем, есть ли уже реальная заявка с таким ID
        const existingAppIndex = prev.findIndex(app => app.id === updatedApp.id);
        if (existingAppIndex !== -1) {
          // Если реальная заявка найдена - просто обновляем её
          console.log('Updating existing real application:', updatedApp.id);
          const newApps = [...prev];
          newApps[existingAppIndex] = updatedApp;
          return newApps;
        }
        
        // Шаг 2: Если реальной заявки нет, ищем временную заявку для "материализации"
        // Ищем по комментарию (имя файла) - это связь между временной и реальной
        const tempAppIndex = prev.findIndex(
          app => app.id < 0 && 
                 app.status === 'PROCESSING' && 
                 app.comment === updatedApp.comment
        );
        
        if (tempAppIndex !== -1) {
          // Нашли соответствующую временную заявку - заменяем её на реальную
          console.log('Materializing temporary application:', prev[tempAppIndex].id, '->', updatedApp.id, 'comment:', updatedApp.comment);
          
          // Очищаем маппинг файла после успешной материализации заявки
          cleanupFilenameMapping(updatedApp.comment);
          
          // Удаляем временный ID из processingApplications
          setProcessingApplications(prevProc => {
            const newSet = new Set(prevProc);
            newSet.delete(prev[tempAppIndex].id);
            return newSet;
          });
          
          const newApps = [...prev];
          newApps[tempAppIndex] = updatedApp;
          return newApps;
        }
        
        // Шаг 3: Если не нашли ни реальную, ни временную с таким комментарием
        // Пробуем найти любую первую временную заявку (fallback на случай если comment не совпал)
        const anyTempAppIndex = prev.findIndex(app => app.id < 0 && app.status === 'PROCESSING');
        if (anyTempAppIndex !== -1) {
          console.log('Materializing first temporary application (fallback):', prev[anyTempAppIndex].id, '->', updatedApp.id);
          
          // Очищаем маппинг файла после успешной материализации заявки
          cleanupFilenameMapping(updatedApp.comment);
          
          setProcessingApplications(prevProc => {
            const newSet = new Set(prevProc);
            newSet.delete(prev[anyTempAppIndex].id);
            return newSet;
          });
          
          const newApps = [...prev];
          newApps[anyTempAppIndex] = updatedApp;
          return newApps;
        }
        
        // Шаг 4: Если вообще ничего не нашли - добавляем новую заявку
        console.log('Application not found, adding new one:', updatedApp.id);
        return [updatedApp, ...prev];
      });
      
      return;
    }
    
    if (message.event === 'file_processing_completed') {
      // Парсим строку JSON в объект ApplicationDetails
      const updatedApplication = JSON.parse(message.application) as ApplicationDetails;
      setApplications(prevApps => {
        // Сначала ищем, есть ли уже заявка с таким реальным ID (на случай, если она была загружена ранее)
        const existingRealAppIndex = prevApps.findIndex(app => app.id === updatedApplication.id);

        if (existingRealAppIndex !== -1) {
          // Если реальная заявка уже есть, обновляем ее        
          const newApps = [...prevApps];
          newApps[existingRealAppIndex] = updatedApplication;   
          // Удаляем ID из processingApplications, если он там был
          setProcessingApplications(prev => {
            const newSet = new Set(prev);
            newSet.delete(updatedApplication.id);
            return newSet;
          });
          
          // Для файлов с отчетом или pd_result автоматически открываем соответствующий вид
          if (updatedApplication.pd_result || updatedApplication.has_pd_result) {
            // Если есть PD pipeline result, переходим на страницу результатов
            navigate(`/applications/${updatedApplication.id}/pd-result`);
          } else if (updatedApplication.report_content || updatedApplication.has_report_content) {
            // Если есть только текстовый отчет, открываем модальное окно
            setReportModal({ isOpen: true, application: updatedApplication });
          } else {
            // Для спецификации переходим на страницу заявки
            navigate(`/applications/${updatedApplication.id}`);
          }
          return newApps;
        } else {
          // Если реальной заявки нет, ищем временную заявку для замены
          // Ищем первую заявку с отрицательным ID и статусом 'PROCESSING'
          const tempAppIndex = prevApps.findIndex(app => app.id < 0 && app.status === 'PROCESSING');

          if (tempAppIndex !== -1) {
            // Удаляем временный ID из processingApplications перед обновлением списка заявок
            setProcessingApplications(prev => {
              const newSet = new Set(prev);
              newSet.delete(prevApps[tempAppIndex].id); // Удаляем именно ID временной заявки
              return newSet;
            });
            // Заменяем временную заявку на реальную
            const newApps = [...prevApps];
            newApps[tempAppIndex] = updatedApplication;
            
            // Для файлов с отчетом или pd_result автоматически открываем соответствующий вид
            if (updatedApplication.pd_result || updatedApplication.has_pd_result) {
              // Если есть PD pipeline result, переходим на страницу результатов
              navigate(`/applications/${updatedApplication.id}/pd-result`);
            } else if (updatedApplication.report_content || updatedApplication.has_report_content) {
              // Если есть только текстовый отчет, открываем модальное окно
              setReportModal({ isOpen: true, application: updatedApplication });
            } else {
              // Для спецификации переходим на страницу заявки
              navigate(`/applications/${updatedApplication.id}`);
            }
            return newApps;
          } else {
            // Если ни реальная, ни временная заявка не найдена, просто добавляем новую
            // Это может произойти, если заявка создана не через загрузку файла, а другим способом
            // Также удаляем ID из processingApplications, если он там был (на случай, если ID совпал с временным)
            setProcessingApplications(prev => {
              const newSet = new Set(prev);
              newSet.delete(updatedApplication.id);
              return newSet;
            });
            
            // Для файлов с отчетом или pd_result автоматически открываем соответствующий вид
            if (updatedApplication.pd_result || updatedApplication.has_pd_result) {
              // Если есть PD pipeline result, переходим на страницу результатов
              navigate(`/applications/${updatedApplication.id}/pd-result`);
            } else if (updatedApplication.report_content || updatedApplication.has_report_content) {
              // Если есть только текстовый отчет, открываем модальное окно
              setReportModal({ isOpen: true, application: updatedApplication });
            } else {
              // Для спецификации переходим на страницу заявки
              navigate(`/applications/${updatedApplication.id}`);
            }
            return [updatedApplication, ...prevApps];
          }
        }
      });
    } else if (message.event === 'file_processing_started') {
      // Добавляем заявку в список обрабатываемых
      const applicationId = message.application_id;
      setProcessingApplications(prev => new Set(prev).add(applicationId));
    } else if (message.event === 'equipment_selection_error') {
      // Обработка ошибки подбора оборудования
      const errorMessage = message.error || message.message || 'Произошла ошибка при подборе оборудования';
      
      if (message.application_id) {
        const applicationId = message.application_id;
        setApplications(prev => prev.filter(app => app.id !== applicationId));
        setProcessingApplications(prev => {
          const newSet = new Set(prev);
          newSet.delete(applicationId);
          return newSet;
        });
      }
      
      alert(errorMessage);
    } else if (message.event === 'file_processing_error') {
      // Обработка ошибок загрузки файлов
      const errorMessage = message.error || message.message || 'Произошла ошибка при обработке';
      const filename = message.filename || '';
      
      // Если есть application_id, удаляем заявку
      if (message.application_id) {
        const applicationId = message.application_id;
        setApplications(prev => prev.filter(app => app.id !== applicationId));
        setProcessingApplications(prev => {
          const newSet = new Set(prev);
          newSet.delete(applicationId);
          return newSet;
        });
      }
      
      // Удаляем все временные заявки в статусе PROCESSING
      setApplications(prev => prev.filter(app => !(app.id < 0 && app.status === 'PROCESSING')));
      setProcessingApplications(prev => {
        const newSet = new Set(prev);
        // Очищаем все отрицательные ID (временные заявки)
        Array.from(newSet).forEach(id => {
          if (id < 0) newSet.delete(id);
        });
        return newSet;
      });
      
      // Показываем ошибку пользователю через uploadErrors
      if (filename) {
        const filenameToFieldMap = filenameToFieldMapRef.current;
        
        // Определяем UI поле для отображения ошибки
        // Приоритет: message.file_type, затем обратная трансляция через маппинг
        let fileType: FileUploadType = message.file_type || 'specification';
        
        // Если бэкенд вернул 'technical_doc', проверяем маппинг для обратной трансляции
        if (fileType === 'technical_doc' && filenameToFieldMap[filename] === 'electrical_scheme') {
          fileType = 'electrical_scheme';
        }
        
        // Fallback: если file_type не был передан, пытаемся определить по расширению
        // Приоритет: сохраненный маппинг (может быть electrical_scheme или technical_doc),
        // затем electrical_scheme как дефолт для обратной совместимости со старым кодом
        if (!message.file_type) {
          const ext = filename.toLowerCase().split('.').pop();
          if (ext && ['pdf', 'dwg', 'jpg', 'png', 'jpeg'].includes(ext)) {
            fileType = filenameToFieldMap[filename] || 'electrical_scheme';
          }
        }
        
        setUploadErrors(prev => ({ 
          ...prev, 
          [fileType]: `Ошибка обработки файла "${filename}": ${errorMessage}` 
        }));
        
        // НЕ очищаем маппинг здесь - он будет очищен при успешном завершении или при следующей загрузке
      }
      
      console.error('Processing error:', errorMessage, filename);
    }
  }, [navigate, cleanupFilenameMapping]);

  const fetchApplications = useCallback(async () => {
    if (!isAuthenticated) {
      setError("Пожалуйста, войдите в систему, чтобы просмотреть свои заявки.");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getMyApplications();
      setApplications(data);
      
    } catch (err) {
      console.error("Failed to fetch user applications:", err);
      if (err instanceof Error) {
        setError(err.message || "Не удалось загрузить заявки. Пожалуйста, попробуйте позже.");
      } else {
        setError("Произошла неизвестная ошибка при загрузке заявок.");
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Добавлен useEffect для обработки  вебсокет-уведомлений
  useEffect(() => {
    webSocketService.addMessageHandler(handleMessage); // Используем addMessageHandler
 
    return () => {
      webSocketService.removeMessageHandler(handleMessage); // Отписываемся от обработчика
    };
  }, [handleMessage]); // Добавляем handleMessage в зависимости

  const groupApplicationsByMonth = (allApps: Application[]) => {
    const grouped: { [key: string]: Array<{ app: Application, globalIndex: number }> } = {};
    // Создаем копию и сортируем ее, чтобы не изменять исходный массив applications
    const sortedApps = [...allApps].sort((a, b) => new Date(b.application_date).getTime() - new Date(a.application_date).getTime());

    sortedApps.forEach((app, globalIndex) => {
        const date = new Date(app.application_date);
        const monthYear = date.toLocaleString('ru-RU', { month: 'long', year: 'numeric' });
        if (!grouped[monthYear]) {
            grouped[monthYear] = [];
        }
        grouped[monthYear].push({ app, globalIndex });
    });
    return grouped;
  };

  const handleCommentUpdated = useCallback((updatedApp: Application) => {
    setApplications(prevApps =>
      prevApps.map(a => (a.id === updatedApp.id ? updatedApp : a))
    );
  }, []);

  const openReport = async (applicationId: number) => {
    const app = applications.find(a => a.id === applicationId);
    if (app && (app.file_type === 'electrical_scheme' || app.file_type === 'technical_doc')) {
      // Загружаем полные данные перед открытием модального окна
      try {
        const fullApp = await ApiService.getApplication(applicationId);
        setReportModal({ isOpen: true, application: fullApp });
      } catch (err) {
        console.error('Failed to load full application data:', err);
      }
    }
  };


  const renderApplicationCard = (item: { app: Application, globalIndex: number }, totalApplicationsCount: number) => {
    const app = item.app;
    const globalIndex = item.globalIndex;
    const isProcessing = processingApplications.has(app.id) || app.status === 'PROCESSING'; // Проверяем, находится ли заявка в процессе обработки

    return (
      <div
        key={app.id}
        className={`group bg-gradient-to-br from-slate-800/95 to-slate-900/95 border-2 border-slate-700/60 backdrop-blur-md p-6 rounded-xl shadow-2xl hover:shadow-sky-500/60 hover:border-sky-500/50 transition-all duration-300 transform hover:-translate-y-1 flex flex-col justify-between overflow-hidden
          ${isProcessing ? 'animate-pulse-custom' : ''}`} // Добавляем класс для мерцания
      >
        <div className="min-w-0">
          <div className="flex justify-between items-start mb-3">
            <h2 className="text-xl font-semibold text-sky-300 group-hover:text-sky-200 transition-colors">
              <HashtagIcon className="inline h-5 w-5 mr-1.5 text-sky-400" />
              Заявка #{totalApplicationsCount - globalIndex}
            </h2>
            
            <div className="flex flex-col items-end gap-2">
              {/* Бейдж статуса разметки схем */}
              {app.has_pd_result && app.is_schemes_processed === false && (
                <span className="text-xs px-2 py-1 rounded-full font-medium bg-orange-500/20 text-orange-300 flex items-center">
                  <ExclamationTriangleIcon className="h-3.5 w-3.5 mr-1" />
                  Требуется разметка схем
                </span>
              )}
              {app.has_pd_result && app.is_schemes_processed === true && (
                <span className="text-xs px-2 py-1 rounded-full font-medium bg-green-500/20 text-green-300 flex items-center">
                  <svg className="h-3.5 w-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Схемы размечены
                </span>
              )}
              
              {/* Надпись "Все готово к подбору" под бейджем схем */}
              {app.has_pd_result && !app.has_spn_results && app.is_schemes_processed === true && (
                <span className="text-xs px-2 py-1 rounded-full font-medium bg-emerald-500/20 text-emerald-300 flex items-center">
                  <svg className="h-3.5 w-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Все готово к подбору
                </span>
              )}
              
              {/* Компонент для отображения статуса обработки */}
              {app.processing_status && (
                <ApplicationStatusBadge status={app.processing_status} />
              )}
            </div>
          </div>

          {isProcessing && ( // Отображаем надпись "ваша заявка создается"
            <p className="text-sm text-yellow-300 mb-2 flex items-center">
              <SparklesIcon className="inline h-4 w-4 mr-2 animate-pulse" />
              Ваша заявка создается...
            </p>
          )}

          <div className="space-y-2 mb-4">
            <p className="text-sm text-slate-300 flex items-center">
              <CalendarDaysIcon className="h-4 w-4 mr-2 text-slate-400" />
              Дата: {new Date(app.application_date).toLocaleString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
            <EditableComment application={app} onCommentUpdated={handleCommentUpdated} />
          </div>
        </div>
        
        {/* Кнопки действий */}
        <div className="mt-auto pt-3 border-t border-slate-700/60 flex flex-wrap justify-between items-center gap-3">
          {/* Кнопка "Открыть анализ ПД" для заявок с pd_result или report_content */}
          {(app.has_pd_result || app.has_report_content) && (
            <button
              onClick={() => {
                if (app.has_pd_result) {
                  navigate(`/applications/${app.id}/pd-result`);
                } else {
                  // Загружаем полные данные при открытии модального окна (т.к. report_content не приходит в списке)
                  ApiService.getApplication(app.id).then(fullApp => {
                    setReportModal({ isOpen: true, application: fullApp });
                  }).catch(err => {
                    console.error('Failed to load full application data:', err);
                  });
                }
              }}
              className="inline-flex items-center bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-2.5 px-5 rounded-lg text-sm shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
            >
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              Открыть анализ ПД
            </button>
          )}
          
          {/* Кнопка для панели подбора спецификации с условной логикой */}
          {app.has_spn_results && !app.processing_status ? (
            <button
              onClick={() => navigate(`/applications/${app.id}?clientAppNumber=${totalApplicationsCount - globalIndex}`)}
              className="inline-flex items-center bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-2.5 px-5 rounded-lg text-sm shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 ml-auto"
            >
              <span>Открыть панель подбора спецификации</span>
              <ArrowRightIcon className="h-5 w-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const handleFileSelect = (fileType: FileUploadType, file: File | null) => {
    if (file) {
      setSelectedFiles(prev => ({ ...prev, [fileType]: file }));
      setUploadErrors(prev => ({ ...prev, [fileType]: null }));
      setUploadSuccessMessages(prev => ({ ...prev, [fileType]: null }));
    }
  };

  const handleUpload = async (fileType: FileUploadType) => {
    console.log(`Starting upload for file type: ${fileType}`); // Добавляем логирование
    const file = selectedFiles[fileType];
    if (!file) {
      setUploadErrors(prev => ({ ...prev, [fileType]: "Пожалуйста, выберите файл для загрузки." }));
      return;
    }

    if (!userId) {
      setUploadErrors(prev => ({ ...prev, [fileType]: "Пользователь не аутентифицирован." }));
      return;
    }

    setUploading(prev => ({ ...prev, [fileType]: true }));
    setUploadErrors(prev => ({ ...prev, [fileType]: null }));
    setUploadSuccessMessages(prev => ({ ...prev, [fileType]: null }));

    // Объединяем логику: electrical_scheme использует тот же процесс что и technical_doc
    // Это сделано потому что оба типа должны проходить через PD pipeline (process_file_with_report)
    // и обрабатываться одинаково на бэкенде. UI сохраняет отдельные поля для удобства пользователя.
    // Другие типы файлов (например, 'specification') передаются без изменений.
    const backendFileType = fileType === 'electrical_scheme' ? 'technical_doc' : fileType;
    trackGoal('upload_click', {
      source: 'my_applications',
      file_type: fileType,
      backend_file_type: backendFileType,
      file_extension: file.name.split('.').pop()?.toLowerCase() || 'unknown',
      file_size_mb: Number((file.size / 1024 / 1024).toFixed(2)),
    });
    
    // Сохраняем маппинг имени файла на UI поле для корректной обработки ошибок WebSocket.
    // Маппинг нужен для всех типов файлов (не только electrical_scheme), т.к. WebSocket
    // может вернуть ошибку без указания file_type, и мы используем маппинг как fallback.
    filenameToFieldMapRef.current[file.name] = fileType;

    // Создаем временную заявку до загрузки с отрицательным ID
    const tempId = Date.now() * -1;
    const tempApplication: Application = {
      id: tempId,
      application_date: new Date().toISOString(),
      comment: file.name,
      status: 'PROCESSING',
      file_type: backendFileType,
      has_spn_results: false,
      has_pd_result: false,
      has_report_content: false
    };
    
    // Добавляем временную заявку в начало списка
    setApplications(prev => [tempApplication, ...prev]);
    
    // Добавляем временный ID в список обрабатываемых заявок
    setProcessingApplications(prev => new Set(prev).add(tempId));

    try {
      console.log(`Calling ApiService.uploadFileWithType with file: ${file.name}, type: ${backendFileType}, userId: ${userId}`); // Добавляем логирование
      const response = await ApiService.uploadFileWithType(file, backendFileType, userId);
      
      // Просто логируем успешную загрузку, реальная заявка придет через WebSocket
      if (response?.application_id) {
        console.log(`File uploaded successfully, application_id: ${response.application_id}`);
      }
      
      setUploadSuccessMessages(prev => ({ ...prev, [fileType]: `Файл "${file.name}" успешно загружен. Заявка создается.` }));
      setSelectedFiles(prev => ({ ...prev, [fileType]: undefined }));
    } catch (err: unknown) {
      console.error("File upload failed:", err);
      
      // Удаляем временную заявку при ошибке
      setApplications(prev => prev.filter(app => app.id !== tempId));
      setProcessingApplications(prev => {
        const newSet = new Set(prev);
        newSet.delete(tempId);
        return newSet;
      });
      
      if (err instanceof Error) {
        setUploadErrors(prev => ({ ...prev, [fileType]: (err as Error).message || "Не удалось загрузить файл. Пожалуйста, попробуйте позже." }));
      } else {
        setUploadErrors(prev => ({ ...prev, [fileType]: "Произошла неизвестная ошибка при загрузке файла." }));
      }
    } finally {
      setUploading(prev => ({ ...prev, [fileType]: false }));
    }
  };

  // Обработчики drag&drop событий
  const handleDragEnter = (e: React.DragEvent, fileType: FileUploadType) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(prev => ({ ...prev, [fileType]: true }));
  };

  const handleDragLeave = (e: React.DragEvent, fileType: FileUploadType) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(prev => ({ ...prev, [fileType]: false }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent, fileType: FileUploadType) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(prev => ({ ...prev, [fileType]: false }));
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(fileType, files[0]);
    }
  };

  const renderFileUploadSection = (config: FileTypeConfig) => {
    const file = selectedFiles[config.key];
    const isUploading = uploading[config.key];
    const error = uploadErrors[config.key];
    const successMessage = uploadSuccessMessages[config.key];
    const dragging = isDragging[config.key];

    const getIcon = () => {
      switch (config.icon) {
        case 'document':
          return <DocumentTextIcon className={`h-6 w-6 ${file ? 'text-green-400' : 'text-sky-400'}`} />;
        case 'bolt':
          return <BoltIcon className={`h-6 w-6 ${file ? 'text-green-400' : 'text-sky-400'}`} />;
        case 'chart':
          return <DocumentChartBarIcon className={`h-6 w-6 ${file ? 'text-green-400' : 'text-sky-400'}`} />;
        default:
          return <DocumentTextIcon className={`h-6 w-6 ${file ? 'text-green-400' : 'text-sky-400'}`} />;
      }
    };

    return (
      <div key={config.key} className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 rounded-xl shadow-2xl overflow-hidden hover:border-sky-500/50 transition-all duration-300">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            {getIcon()}
            <div>
              <h3 className="text-lg font-semibold text-sky-300">{config.title}</h3>
              <p className="text-xs text-slate-400">{config.description}</p>
            </div>
          </div>
          
          <div
            className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors duration-200
                        ${dragging ? 'border-sky-500 bg-sky-500/10' : 'border-slate-600 hover:border-sky-400'}
                        ${file ? 'border-green-500 bg-green-500/10' : ''}`}
            onClick={() => document.getElementById(`fileUpload-${config.key}`)?.click()}
            onDragEnter={(e) => handleDragEnter(e, config.key)}
            onDragLeave={(e) => handleDragLeave(e, config.key)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, config.key)}
          >
            <input
              type="file"
              id={`fileUpload-${config.key}`}
              className="hidden"
              onChange={(e) => handleFileSelect(config.key, e.target.files?.[0] || null)}
              accept={config.acceptedFormats}
            />
            
            <ArrowUpTrayIcon className={`h-8 w-8 mx-auto mb-2 ${file ? 'text-green-400' : 'text-slate-400'}`} />
            <p className={`text-sm font-medium ${file ? 'text-green-300' : 'text-slate-100'}`}>
              {file ? `${file.name}` : "Выберите файл"}
            </p>
            {file && (
              <p className="text-xs text-slate-400 mt-1">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            )}
            {!file && (
              <p className="text-xs text-slate-400 mt-1">
                {config.acceptedFormats.split(',').join(', ')}
              </p>
            )}
          </div>

          {error && (
            <p className="mt-2 text-xs text-red-400 flex items-center">
              <ExclamationTriangleIcon className="inline h-4 w-4 mr-1" />
              {error}
            </p>
          )}
          {successMessage && (
            <p className="mt-2 text-xs text-green-400 flex items-center">
              <SparklesIcon className="inline h-4 w-4 mr-1" />
              {successMessage}
            </p>
          )}

          {file && !isUploading && (
            <button
              onClick={() => handleUpload(config.key)}
              className="mt-3 w-full inline-flex items-center justify-center bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white font-semibold py-3 px-4 rounded-lg text-sm shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300"
            >
              <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
              Загрузить
            </button>
          )}
          {isUploading && (
            <div className="mt-3 w-full flex items-center justify-center text-sky-300 py-2">
              <SparklesIcon className="h-4 w-4 animate-spin mr-2" />
              <span className="text-sm">Загрузка...</span>
            </div>
          )}
        </div>
      </div>
    );
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900 text-slate-100 flex flex-col items-center justify-center p-6">
        <SparklesIcon className="h-12 w-12 text-sky-400 animate-spin mb-4" />
        <p className="text-xl text-slate-200">Загрузка ваших заявок...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900 text-slate-100 flex flex-col items-center justify-center p-6 text-center">
        <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mb-4" />
        <h2 className="text-2xl font-semibold text-red-300 mb-2">Ошибка при загрузке</h2>
        <p className="text-slate-200 max-w-md">{error}</p>
        <Link
            to="/"
            className="mt-6 inline-flex items-center bg-sky-500 hover:bg-sky-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300"
        >
            Вернуться на главную
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900 text-slate-200 p-4 sm:p-6">
      <div className="container mx-auto">
        
        {/* Компонент загрузки файлов */}
        <div className="mb-10">
          <h2 className="text-3xl font-bold text-sky-300 mb-6">Создать заявку по файлу</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {fileTypeConfigs.map(config => renderFileUploadSection(config))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-sky-300 mb-4 sm:mb-0">
            Мои заявки
          </h1>
        </div>

        {applications.length === 0 && !loading ? ( // Добавил !loading чтобы не показывать "нет заявок" во время первоначальной загрузки
          <div className="text-center py-16 bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 rounded-xl shadow-2xl">
            <InboxIcon className="h-24 w-24 text-sky-500 mx-auto mb-6" />
            <h2 className="text-2xl font-semibold text-slate-100 mb-3">У вас пока нет заявок</h2>
            <p className="text-slate-300 mb-6 max-w-md mx-auto">
              Здесь будут отображаться все ваши созданные заявки на подбор продукции.
            </p>
          </div>
        ) : (
          <div>
            {Object.entries(groupApplicationsByMonth(applications)).map(([monthYear, appsInMonth]) => (
              <div key={monthYear} className="mb-8">
                <h2 className="text-2xl font-semibold text-sky-400 mb-4 capitalize">{monthYear}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pr-2">
                  {appsInMonth.map((item) => renderApplicationCard(item, applications.length))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Report Modal */}
      {reportModal.isOpen && reportModal.application && (
        <ReportModal
          isOpen={reportModal.isOpen}
          onClose={() => setReportModal({ isOpen: false, application: null })}
          applicationId={reportModal.application.id}
          userId={userId || 0}
          fileType={reportModal.application.file_type === 'electrical_scheme' || reportModal.application.file_type === 'technical_doc' 
            ? reportModal.application.file_type 
            : undefined}
          onProcessingStart={() => {
            setReportModal({ isOpen: false, application: null });
            fetchApplications();
          }}
        />
      )}
    </div>
  );
};

export default UserApplicationsPage;
